import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { ClientMsg } from "@shared/protocol";
import type { GameStateView, Language } from "@shared/types";
import { LogoMark } from "../components/Brand";
import { Icon } from "../components/Icon";
import { LanguagePicker, ThemePicker } from "../components/Pickers";
import { QuestionImage } from "../components/QuestionImage";
import { RoomFrame } from "../components/RoomFrame";
import { ShareModal } from "../components/ShareCard";
import { Standings } from "../components/Standings";
import {
  Button,
  IconButton,
  LinkButton,
  Modal,
  RoomCode,
  useDialog,
} from "../components/ui";
import { useFx } from "../lib/fx";
import { useT } from "../lib/i18n";
import { useRoom } from "../lib/room";
import {
  hostTokenFor,
  listHostedRooms,
  newDraft,
  rememberHostedRoom,
  saveDraft,
} from "../lib/storage";

// ─────────────────────────────────────────────────────────────────────────────
//  The admin screen. Everything that can change the game lives here, gated by a
//  per-room token — there is no shared PIN, so knowing the room code gets a
//  guest into the game but never into the controls.
// ─────────────────────────────────────────────────────────────────────────────

export default function Host() {
  const { code = "" } = useParams<{ code: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // A host link can carry its token (`?t=…`) so the host can move to another
  // device — or hand the controls over — by sharing one URL. We store it and
  // strip it from the address bar so it does not linger in history or a screen
  // share.
  const [token, setToken] = useState<string | null>(() => hostTokenFor(code));
  useEffect(() => {
    const fromUrl = searchParams.get("t");
    if (!fromUrl) return;
    const known = listHostedRooms().find((r) => r.code === code);
    rememberHostedRoom({
      code,
      hostToken: fromUrl,
      title: known?.title ?? "OSJ",
      createdAt: known?.createdAt ?? Date.now(),
    });
    setToken(fromUrl);
    searchParams.delete("t");
    setSearchParams(searchParams, { replace: true });
  }, [code, searchParams, setSearchParams]);

  const conn = useRoom(code, "host", token);
  const toast = useFx();

  if (!token) return <NoTokenScreen code={code} />;

  return (
    <RoomFrame state={conn.state} status={conn.status} fatal={conn.fatal}>
      {conn.state && <HostBoard game={conn.state} send={conn.send} toast={toast} code={code} />}
    </RoomFrame>
  );
}

function NoTokenScreen({ code }: { code: string }) {
  const t = useT();
  return (
    <div className="min-h-full flex flex-col items-center justify-center gap-5 p-8 text-center">
      <Icon name="key" size="2.5rem" className="text-ink/30" />
      <p className="text-lg text-ink/70 max-w-sm">{t("badToken")}</p>
      <div className="flex gap-2">
        <Link
          to={`/r/${code}/play`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-control bg-accent text-onAccent font-semibold hover:bg-accentLight transition-colors"
        >
          {t("joinRoom")}
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-control bg-ink/10 border border-ink/15 font-semibold hover:bg-ink/[0.17] transition-colors"
        >
          {t("appName")}
        </Link>
      </div>
    </div>
  );
}

type Send = (msg: ClientMsg) => void;

function HostBoard({
  game,
  send,
  toast,
  code,
}: {
  game: GameStateView;
  send: Send;
  toast: ReturnType<typeof useFx>;
  code: string;
}) {
  const t = useT();
  const [showShare, setShowShare] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // The standings, the final screen and the wager round all show scores
  // themselves — the side scoreboard would just repeat them.
  const fullScreen =
    game.phase === "intermission" || game.phase === "final" || game.phase === "wager";
  const nextIsWager = game.hasWagerRound && game.roundIndex >= game.totalRounds - 2;

  return (
    <div className="min-h-full flex flex-col lg:flex-row">
      <div className="flex-1 flex flex-col min-w-0">
        <header className="flex items-center gap-2 px-4 py-3 border-b border-line shrink-0">
          <LogoMark size={24} />
          <span className="type-eyebrow text-ink/40 hidden sm:inline">{t("adminTitle")}</span>
          <RoomCode code={code} className="text-accentLight" />
          <span className="text-ink/30 text-sm hidden md:inline truncate">{game.roundName}</span>
          <div className="flex-1" />
          <Button size="sm" icon="qr" onClick={() => setShowShare(true)}>
            <span className="hidden sm:inline">{t("showQR")}</span>
          </Button>
          <LinkButton
            size="sm"
            icon="monitor"
            href={`/r/${code}/show`}
            target="_blank"
            rel="noreferrer"
          >
            <span className="hidden sm:inline">{t("openShowcase")}</span>
          </LinkButton>
          <IconButton
            size="sm"
            variant="secondary"
            icon="settings"
            label={t("settings")}
            onClick={() => setShowSettings(true)}
          />
        </header>

        {game.phase === "question" ? (
          <QuestionPanel game={game} send={send} />
        ) : game.phase === "wager" ? (
          <WagerAdmin game={game} send={send} />
        ) : game.phase === "intermission" ? (
          <Standings
            game={game}
            action={
              <Button
                variant="primary"
                size="xl"
                iconRight="arrowRight"
                onClick={() => send({ t: "host:nextRound" })}
              >
                {nextIsWager ? t("toFinalRound") : t("nextRound")}
              </Button>
            }
          />
        ) : game.phase === "final" ? (
          <Standings
            game={game}
            action={
              <Button
                variant="primary"
                size="xl"
                icon="rotate"
                onClick={() => send({ t: "host:resetGame" })}
              >
                {t("newGame")}
              </Button>
            }
          />
        ) : (
          <Board game={game} send={send} />
        )}
      </div>

      {!fullScreen && <Scoreboard game={game} send={send} />}

      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-successStrong text-white font-semibold shadow-pop anim-pop">
          <Icon name="party" />
          {toast.name} +{toast.points}
        </div>
      )}

      {showShare && <ShareModal code={code} onClose={() => setShowShare(false)} />}
      <SettingsModal
        open={showSettings}
        onClose={() => setShowSettings(false)}
        game={game}
        send={send}
        code={code}
      />
    </div>
  );
}

// ───────────────────────────── settings ─────────────────────────────

function SettingsModal({
  open,
  onClose,
  game,
  send,
  code,
}: {
  open: boolean;
  onClose: () => void;
  game: GameStateView;
  send: Send;
  code: string;
}) {
  const t = useT();
  const navigate = useNavigate();
  const dialog = useDialog();

  // "Edit questions" seeds a draft from the pack the room is actually running,
  // then reopens the editor bound to this room so saving pushes it back.
  const editQuestions = async () => {
    if (!game.pack) return;
    const ok = await dialog.confirm({
      title: t("editQuestions"),
      body: t("editQuestionsWarn"),
      confirmLabel: t("editQuestions"),
      cancelLabel: t("cancel"),
    });
    if (!ok) return;
    const draft = newDraft(JSON.parse(JSON.stringify(game.pack)));
    saveDraft(draft);
    navigate(`/edit/${draft.id}?room=${code}`);
  };

  return (
    <Modal open={open} onClose={onClose} title={t("settings")} width="max-w-lg">
      <div className="flex flex-col gap-6">
        <ThemePicker
          value={game.settings.theme}
          onChange={(theme) => send({ t: "host:setTheme", theme })}
        />

        <div>
          <div className="type-eyebrow text-ink/45 mb-2 flex items-center gap-1.5">
            <Icon name="globe" size="0.9rem" />
            {t("language")}
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <div className="text-xs text-ink/35 mb-1.5">{t("languageThisDevice")}</div>
              <LanguagePicker />
            </div>
            <div>
              <div className="text-xs text-ink/35 mb-1.5">{t("languageRoomDefault")}</div>
              <div className="flex gap-1.5">
                {(["en", "de"] as Language[]).map((l) => (
                  <Button
                    key={l}
                    size="sm"
                    variant={game.settings.language === l ? "primary" : "secondary"}
                    aria-pressed={game.settings.language === l}
                    onClick={() => send({ t: "host:setLanguage", language: l })}
                  >
                    {l.toUpperCase()}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {game.pack && game.pack.rounds.length > 1 && (
          <div>
            <div className="type-eyebrow text-ink/45 mb-2">{t("jumpToRound")}</div>
            <div className="flex flex-wrap gap-1.5">
              {game.pack.rounds.map((r, i) => (
                <Button
                  key={i}
                  size="sm"
                  variant={
                    game.phase !== "wager" && game.roundIndex === i ? "primary" : "secondary"
                  }
                  onClick={() => send({ t: "host:goToRound", roundIndex: i })}
                >
                  {r.name}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-1 border-t border-line pt-4">
          <Button icon="pencil" onClick={editQuestions}>
            {t("editQuestions")}
          </Button>
          <Button
            variant="danger"
            icon="rotate"
            onClick={async () => {
              const ok = await dialog.confirm({
                title: t("resetGame"),
                body: t("resetGameConfirm"),
                confirmLabel: t("resetGame"),
                cancelLabel: t("cancel"),
                destructive: true,
              });
              if (ok) send({ t: "host:resetGame" });
            }}
          >
            {t("resetGame")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ───────────────────────────── board ─────────────────────────────

function Board({ game, send }: { game: GameStateView; send: Send }) {
  const t = useT();
  const dialog = useDialog();
  const cols = game.categories.length || 1;

  return (
    <div className="flex-1 p-4 flex flex-col min-h-0 gap-3">
      <div
        className="grid gap-2.5 flex-1 min-h-0"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      >
        {game.categories.map((cat) => (
          <div key={cat.id} className="flex flex-col gap-2.5 min-h-0">
            <div className="bg-boardDark rounded-card px-2 py-3 text-center type-display text-sm sm:text-base uppercase min-h-[60px] flex items-center justify-center border-b-2 border-accent/40">
              {cat.name}
            </div>
            {cat.cells.map((cell) => (
              <button
                key={cell.points}
                onClick={() => send({ t: "host:reveal", categoryId: cat.id, points: cell.points })}
                title={cell.used ? t("replayHint") : undefined}
                className={`flex-1 rounded-card type-display text-2xl sm:text-4xl tabular transition-all duration-100 ease-pop ${
                  cell.used
                    ? "bg-board/12 text-ink/20 hover:bg-board/25 hover:text-ink/40"
                    : "bg-board text-onBoard shadow-tile hover:brightness-110 hover:-translate-y-0.5"
                }`}
              >
                {cell.points}
              </button>
            ))}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm shrink-0">
        <span className="text-ink/25">{t("replayHint")}</span>
        <Button
          size="sm"
          variant="ghost"
          icon="skip"
          onClick={async () => {
            const ok = await dialog.confirm({
              title: t("jumpToStandings"),
              body: t("jumpToStandingsConfirm"),
              confirmLabel: t("jumpToStandings"),
              cancelLabel: t("cancel"),
            });
            if (ok) send({ t: "host:forceIntermission" });
          }}
        >
          {t("jumpToStandings")}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          icon="flag"
          onClick={async () => {
            const ok = await dialog.confirm({
              title: t("endGame"),
              body: t("endGameConfirm"),
              confirmLabel: t("endGame"),
              cancelLabel: t("cancel"),
              destructive: true,
            });
            if (ok) send({ t: "host:toFinal" });
          }}
        >
          {t("endGame")}
        </Button>
      </div>
    </div>
  );
}

// ───────────────────────── question control panel ─────────────────────────

function QuestionPanel({ game, send }: { game: GameStateView; send: Send }) {
  const t = useT();
  const q = game.current;
  const first = game.buzzes[0];

  const lockedNames = useMemo(
    () =>
      game.lockedOut
        .map((id) => game.players.find((p) => p.id === id)?.name)
        .filter((n): n is string => Boolean(n)),
    [game.lockedOut, game.players],
  );
  const allLocked =
    game.players.length > 0 && game.players.every((p) => game.lockedOut.includes(p.id));

  if (!q) return null;

  return (
    <div className="flex-1 p-4 sm:p-5 flex flex-col min-h-0 gap-4">
      <div className="flex items-center gap-3 shrink-0">
        <Button size="sm" variant="ghost" icon="arrowLeft" onClick={() => send({ t: "host:backToBoard" })}>
          {t("board")}
        </Button>
        <div className="flex-1" />
        <div className="type-eyebrow text-accent">
          {q.categoryName} · {q.points}
        </div>
      </div>

      {/* The question is the hero, but sized to its content — a one-line
          question stretched to fill the screen reads as a layout bug. */}
      <div className="flex-1 min-h-0 flex items-center justify-center">
        <div className="bg-board rounded-surface px-8 py-10 w-full max-w-4xl flex flex-col items-center justify-center gap-5 text-center shadow-pop min-h-[30vh] max-h-full overflow-y-auto">
          <QuestionImage src={q.image} className="max-h-[28vh] w-auto rounded-card object-contain" />
          <p className="type-display text-3xl sm:text-5xl leading-[1.08]">{q.question}</p>
        </div>
      </div>

      {/* The answer is a host-only aid — legible, never a headline. */}
      <div className="shrink-0 flex items-center justify-center gap-3 flex-wrap">
        <span className="type-eyebrow text-ink/35">{t("answer")}</span>
        <span className="text-accentLight font-semibold text-lg">{q.answer}</span>
        {q.answerRevealed && (
          <span className="inline-flex items-center gap-1 text-success text-xs">
            <Icon name="eye" size="0.85rem" />
            {t("visibleToPlayers")}
          </span>
        )}
      </div>

      {/* Judging: the one thing the host reaches for under time pressure. */}
      <div className="shrink-0 min-h-[76px] flex items-center">
        {first ? (
          <div className="flex items-center gap-4 w-full flex-wrap anim-pop">
            <div className="flex items-center gap-3 min-w-0 shrink-0">
              <img src={first.avatarUrl} alt="" className="w-12 h-12 rounded-card bg-white" />
              <div className="min-w-0">
                <div className="type-eyebrow text-ink/35">{t("onTheClock")}</div>
                <div className="type-display text-2xl text-accentLight truncate max-w-[180px]">
                  {first.name}
                </div>
              </div>
            </div>
            <div className="flex-1 flex gap-3 min-w-[240px]">
              <Button
                variant="success"
                size="xl"
                icon="check"
                className="flex-1"
                onClick={() => send({ t: "host:judge", playerId: first.playerId, correct: true })}
              >
                {t("correct")}
              </Button>
              <Button
                variant="danger"
                size="xl"
                icon="x"
                className="flex-1"
                onClick={() => send({ t: "host:judge", playerId: first.playerId, correct: false })}
              >
                {t("wrong")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="w-full flex items-center justify-center gap-3 flex-wrap">
            {game.buzzersOpen ? (
              allLocked ? (
                <span className="text-ink/55">{t("allLockedOut")}</span>
              ) : (
                <span className="text-success inline-flex items-center gap-2 font-medium">
                  <span className="w-2.5 h-2.5 rounded-full bg-success animate-pulse" />
                  {t("buzzersLive")}
                </span>
              )
            ) : (
              <span className="text-ink/35">{t("buzzersClosed")}</span>
            )}
          </div>
        )}
      </div>

      <div className="shrink-0 flex flex-wrap items-center justify-center gap-2 text-sm">
        {!q.answerRevealed && (
          <Button size="sm" icon="eye" onClick={() => send({ t: "host:revealAnswer" })}>
            {t("showAnswer")}
          </Button>
        )}
        {game.buzzersOpen ? (
          <Button size="sm" icon="pause" onClick={() => send({ t: "host:closeBuzzers" })}>
            {t("closeBuzzers")}
          </Button>
        ) : (
          <Button size="sm" icon="bell" onClick={() => send({ t: "host:openBuzzers" })}>
            {t("openBuzzers")}
          </Button>
        )}
        <Button size="sm" icon="rotate" onClick={() => send({ t: "host:resetBuzzers" })}>
          {t("resetBuzzers")}
        </Button>
        {lockedNames.length > 0 && (
          <span className="text-ink/30 text-xs">
            {t("lockedOutLabel")} {lockedNames.join(", ")}
          </span>
        )}
      </div>
    </div>
  );
}

// ───────────────────────── wager round (admin) ─────────────────────────

function WagerAdmin({ game, send }: { game: GameStateView; send: Send }) {
  const t = useT();
  const w = game.wager;
  if (!w) return null;

  const stageLabel =
    w.stage === "betting" ? t("wagers") : w.stage === "question" ? t("answers") : t("reveal");

  return (
    <div className="flex-1 p-4 sm:p-5 flex flex-col min-h-0 gap-4">
      <div className="type-eyebrow text-accent text-center shrink-0">
        {w.name} · {stageLabel}
      </div>

      {w.stage !== "betting" && (
        <div className="bg-board rounded-surface px-6 py-6 text-center shrink-0 shadow-pop flex flex-col items-center gap-3">
          <QuestionImage src={w.image} className="max-h-[24vh] w-auto rounded-card object-contain" />
          <p className="type-display text-2xl sm:text-4xl leading-[1.1]">{w.question}</p>
          {w.answer && (
            <p className="flex items-center gap-2.5 mt-2">
              <span className="type-eyebrow text-ink/35">{t("solution")}</span>
              <span className="text-accentLight font-semibold text-lg">{w.answer}</span>
            </p>
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto flex flex-col gap-2">
        {w.entries.length === 0 && <p className="text-ink/40 text-center py-6">{t("noPlayers")}</p>}
        {w.entries
          .filter((e) => w.stage !== "reveal" || e.wagered || e.answered)
          .map((e) => (
            <div
              key={e.playerId}
              className={`rounded-card p-3 flex items-center gap-3 flex-wrap ${
                e.judged === "correct"
                  ? "bg-successStrong/25 border border-success/30"
                  : e.judged === "wrong"
                    ? "bg-dangerStrong/25 border border-danger/30"
                    : "bg-ink/[0.05] border border-line"
              }`}
            >
              <img src={e.avatarUrl} alt="" className="w-10 h-10 rounded-control bg-white" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">
                  {e.name} <span className="text-ink/35 text-sm tabular">({e.score})</span>
                </div>
                {w.stage === "reveal" ? (
                  <div className="text-accentLight font-semibold break-words">
                    “{e.answer || "—"}” · {t("wagerLabel")} {e.wager}
                  </div>
                ) : (
                  <div className="text-sm text-ink/45 inline-flex items-center gap-1.5">
                    {(w.stage === "betting" ? e.wagered : e.answered) && (
                      <Icon name="check" className="text-success" size="0.85rem" />
                    )}
                    {w.stage === "betting"
                      ? e.wagered
                        ? t("wagerPlaced")
                        : t("waitingForWager")
                      : e.answered
                        ? t("answerSubmitted")
                        : t("typing")}
                  </div>
                )}
              </div>
              {w.stage === "reveal" && (
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="md"
                    variant={e.judged === "correct" ? "success" : "secondary"}
                    icon="check"
                    onClick={() => send({ t: "host:wagerJudge", playerId: e.playerId, correct: true })}
                  >
                    <span className="tabular">+{e.wager}</span>
                  </Button>
                  <Button
                    size="md"
                    variant={e.judged === "wrong" ? "danger" : "secondary"}
                    icon="x"
                    onClick={() => send({ t: "host:wagerJudge", playerId: e.playerId, correct: false })}
                  >
                    <span className="tabular">−{e.wager}</span>
                  </Button>
                </div>
              )}
            </div>
          ))}
      </div>

      <div className="shrink-0 flex justify-center">
        {w.stage === "betting" && (
          <Button
            variant="primary"
            size="xl"
            iconRight="arrowRight"
            onClick={() => send({ t: "host:wagerShowQuestion" })}
          >
            {t("showQuestion")}
          </Button>
        )}
        {w.stage === "question" && (
          <Button
            variant="primary"
            size="xl"
            iconRight="arrowRight"
            onClick={() => send({ t: "host:wagerReveal" })}
          >
            {t("revealAnswers")}
          </Button>
        )}
        {w.stage === "reveal" && (
          <Button
            variant="primary"
            size="xl"
            iconRight="arrowRight"
            onClick={() => send({ t: "host:toFinal" })}
          >
            {t("toFinalScores")}
          </Button>
        )}
      </div>
    </div>
  );
}

// ───────────────────────────── scoreboard ─────────────────────────────

function Scoreboard({ game, send }: { game: GameStateView; send: Send }) {
  const t = useT();
  const dialog = useDialog();

  return (
    <aside className="lg:w-[19rem] shrink-0 border-t lg:border-t-0 lg:border-l border-line flex flex-col lg:overflow-y-auto">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-ink/[0.07]">
        <Icon name="users" className="text-ink/35" />
        <h2 className="type-eyebrow text-ink/45">{t("players")}</h2>
        <span className="text-ink/25 text-xs tabular ml-auto">{game.players.length}</span>
      </div>

      {game.players.length === 0 && (
        <p className="text-ink/35 text-sm p-4 leading-relaxed">{t("noPlayersHint")}</p>
      )}

      <div className="flex flex-col gap-1.5 p-2.5">
        {game.players.map((p, i) => (
          <div
            key={p.id}
            className="group bg-ink/[0.05] hover:bg-ink/[0.09] rounded-card p-2 flex items-center gap-2 transition-colors"
          >
            <span className="type-code text-xs text-ink/25 w-4 text-center">{i + 1}</span>
            <div className="relative shrink-0">
              <img src={p.avatarUrl} alt="" className="w-9 h-9 rounded-control bg-white" />
              {!p.connected && (
                <span
                  className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-ink/40 border-2"
                  style={{ borderColor: "var(--c-panel)" }}
                  title={t("offline")}
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate text-sm leading-tight">{p.name}</div>
              <button
                onClick={async () => {
                  const v = await dialog.prompt({
                    title: t("editScore"),
                    body: t("setScorePrompt", { name: p.name }),
                    defaultValue: String(p.score),
                    confirmLabel: t("save"),
                    cancelLabel: t("cancel"),
                  });
                  if (v !== null && v.trim() !== "" && Number.isFinite(Number(v))) {
                    send({ t: "host:setScore", playerId: p.id, score: Number(v) });
                  }
                }}
                title={t("editScore")}
                className={`text-lg font-bold tabular text-left hover:underline underline-offset-2 ${
                  p.score < 0 ? "text-danger" : "text-accentLight"
                }`}
              >
                {p.score}
              </button>
            </div>
            <div className="flex flex-col gap-0.5">
              <IconButton
                size="sm"
                icon="plus"
                label={`+100 ${p.name}`}
                className="h-6 w-7 text-success hover:bg-success/15"
                onClick={() => send({ t: "host:adjustScore", playerId: p.id, delta: 100 })}
              />
              <IconButton
                size="sm"
                icon="minus"
                label={`-100 ${p.name}`}
                className="h-6 w-7 text-danger hover:bg-danger/15"
                onClick={() => send({ t: "host:adjustScore", playerId: p.id, delta: -100 })}
              />
            </div>
            <IconButton
              size="sm"
              icon="x"
              label={t("removePlayer")}
              className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-ink/40 hover:text-danger"
              onClick={async () => {
                const ok = await dialog.confirm({
                  title: t("removePlayer"),
                  body: t("removePlayerConfirm", { name: p.name }),
                  confirmLabel: t("removePlayer"),
                  cancelLabel: t("cancel"),
                  destructive: true,
                });
                if (ok) send({ t: "host:kick", playerId: p.id });
              }}
            />
          </div>
        ))}
      </div>
    </aside>
  );
}
